/**
 * Shared components and utilities for EventCard and EventThemeCard
 * Contains common rendering logic to avoid duplication
 */

'use client';

import { buttonVariants } from '@/components/ui/Button';
import { darkenHexColorWithOpacity } from '@/utils/colorService';
import { ArrowRight, Calendar, Clock, ExternalLink, MapPin, Tag, User } from 'lucide-react';
import Link from 'next/link';
import type { EventTeaser } from '../types';
import { AddToCalendar } from './add-to-calendar';

export interface EventCardSharedProps {
  event: EventTeaser;
  eventUrl: string;
  variant: 'default' | 'compact';
  showPastIndicator?: boolean;
}

export interface EventCardData {
  startDate: Date | null;
  endDate: Date | null;
  isPast: boolean;
  isToday: boolean;
  excerpt: string;
  eventTypeColor?: string;
  firstEventType: { id: string; name: string } | null;
}

/**
 * Extract and process event data for card rendering
 */
export function useEventCardData(event: EventTeaser): EventCardData {
  const startDate = event.eventDateTimeRange?.start?.timestamp
    ? new Date(event.eventDateTimeRange.start.timestamp * 1000)
    : null;
  const endDate = event.eventDateTimeRange?.end?.timestamp
    ? new Date(event.eventDateTimeRange.end.timestamp * 1000)
    : startDate;

  const now = new Date();
  const isPast = endDate ? endDate < now : false;
  const isToday = startDate ? startDate.toDateString() === now.toDateString() : false;

  // Extract text from HTML body
  const getTextExcerpt = (html: string | null | undefined, maxLength = 150) => {
    if (!html) return '';
    const text = html.replace(/<[^>]*>/g, '');
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

  const excerpt = event.body?.summary || getTextExcerpt(event.body?.processed);

  // Extract eventTypeColor from eventType
  const eventTypeObj = event.eventType as { eventTypeColor?: string | null } | null | undefined;
  const eventTypeColor = eventTypeObj?.eventTypeColor ?? undefined;

  // Get first eventType for display (normalize if needed for compatibility)
  const normalizedEventType = event.eventType
    ? Array.isArray(event.eventType)
      ? event.eventType
      : [event.eventType]
    : [];
  const firstEventType = normalizedEventType[0] || null;

  return {
    startDate,
    endDate,
    isPast,
    isToday,
    excerpt,
    eventTypeColor,
    firstEventType,
  };
}

/**
 * Format date for display
 */
export function formatEventDate(date: Date): string {
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Format time for display
 */
export function formatEventTime(date: Date): string {
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Date badge component with year
 */
export function EventDateBadge({
  date,
  size = 'default',
  className = '',
}: {
  date: Date;
  size?: 'default' | 'compact' | 'large';
  className?: string;
}) {
  const month = date.toLocaleDateString('en-GB', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();

  const sizeConfig = {
    default: {
      container: 'h-[72px] w-16',
      month: 'text-xs',
      day: 'text-2xl',
      year: 'text-[10px]',
    },
    compact: {
      container: 'h-[72px] w-16',
      month: 'text-xs',
      day: 'text-2xl',
      year: 'text-[10px]',
    },
    large: {
      container: 'h-24 w-20',
      month: 'text-xs',
      day: 'text-3xl',
      year: 'text-[10px]',
    },
  };

  const config = sizeConfig[size];

  return (
    <div className={`flex flex-col items-center justify-center rounded-lg bg-primary/10 text-center ${config.container} ${className}`}>
      <div className={`font-medium text-muted-foreground ${config.month}`}>{month}</div>
      <div className={`font-bold text-primary ${config.day}`}>{day}</div>
      <div className={`font-medium text-muted-foreground ${config.year}`}>{year}</div>
    </div>
  );
}

/**
 * Date badge overlay for images
 */
export function EventDateBadgeOverlay({
  date,
  className = '',
}: {
  date: Date;
  className?: string;
}) {
  const month = date.toLocaleDateString('en-GB', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();

  return (
    <div className={`absolute left-4 top-4 flex h-[72px] w-16 flex-col items-center justify-center rounded-lg bg-background/90 backdrop-blur-sm text-center shadow-md ${className}`}>
      <div className="text-xs font-medium text-muted-foreground">{month}</div>
      <div className="text-2xl font-bold text-primary">{day}</div>
      <div className="text-[10px] font-medium text-muted-foreground">{year}</div>
    </div>
  );
}

/**
 * Compact card layout
 */
export function EventCardCompact({
  event,
  eventUrl,
  data,
}: {
  event: EventTeaser;
  eventUrl: string;
  data: EventCardData;
}) {
  const { startDate, endDate, eventTypeColor } = data;

  return (
    <Link
      href={eventUrl}
      className={`group flex h-full w-full flex-col overflow-hidden rounded-lg border p-3 shadow-sm transition-all hover:shadow-md ${
        eventTypeColor ? '' : 'bg-card'
      }`}
      style={{
        backgroundColor: eventTypeColor
          ? darkenHexColorWithOpacity(eventTypeColor, 80, 0.1)
          : undefined,
      }}
    >
      <div className="flex flex-col gap-3 min-w-0">
        <div className="flex gap-3 min-w-0">
          {/* Date badge */}
          {startDate && (
            <EventDateBadge date={startDate} size="compact" className="shrink-0" />
          )}
          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="mb-1 line-clamp-2 text-sm font-semibold leading-tight transition-colors group-hover:text-primary break-words">
              {event.title}
            </h3>

            {event.eventLocation && (
              <div className="mb-1 flex items-center gap-1 text-xs text-muted-foreground min-w-0">
                <MapPin className="h-3 w-3 shrink-0" />
                <span className="truncate min-w-0">{event.eventLocation}</span>
              </div>
            )}

            {startDate && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3 shrink-0" style={{ color: eventTypeColor || undefined }} />
                <span className="truncate">{formatEventTime(startDate)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Add to Calendar Button */}
        {startDate && (
          <div className="mt-auto pt-2 border-t">
            <AddToCalendar
              event={{
                title: event.title,
                startDate: startDate,
                endDate: endDate,
                location: event.eventLocation || null,
                description: null,
                url: eventUrl || null,
              }}
              variant="ghost"
              size="sm"
              className="w-full text-xs"
            />
          </div>
        )}
      </div>
    </Link>
  );
}

/**
 * Default card layout
 */
export function EventCardDefault({
  event,
  eventUrl,
  data,
  showPastIndicator = true,
}: {
  event: EventTeaser;
  eventUrl: string;
  data: EventCardData;
  showPastIndicator?: boolean;
}) {
  const { startDate, endDate, isPast, isToday, excerpt, eventTypeColor, firstEventType } = data;

  return (
    <Link
      href={eventUrl}
      className={`group flex h-full w-full flex-col overflow-hidden rounded-lg border shadow-sm transition-all hover:shadow-lg ${
        eventTypeColor ? '' : 'bg-card'
      }`}
      style={{
        backgroundColor: eventTypeColor
          ? darkenHexColorWithOpacity(eventTypeColor, 80, 0.1)
          : undefined,
      }}
    >
      {/* Image or date badge */}
      {event.image ? (
        <div className="relative h-24 p-2">
          {/* <Image
            src={event.image.url}
            width={event.image.width ?? 800}
            height={event.image.height ?? 450}
            alt={event.image.alt || event.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          /> */}

          {/* Date badge overlay */}
          {startDate && <EventDateBadgeOverlay date={startDate} />}

          {/* Status badges */}
          <div className="absolute right-4 top-4 flex flex-col gap-2">
            {isToday && (
              <span className="rounded bg-accent px-2 py-1 text-xs font-medium text-accent-foreground shadow-md">
                Today
              </span>
            )}
            {isPast && showPastIndicator && (
              <span className="rounded bg-muted px-2 py-1 text-xs font-medium text-muted-foreground shadow-md">
                Past
              </span>
            )}
          </div>
        </div>
      ) : startDate ? (
        <div className="flex h-32 items-center justify-center bg-linear-to-br from-primary/10 to-primary/5">
          <EventDateBadge date={startDate} size="large" className="shadow-md" />
        </div>
      ) : null}
      {/* Content */}
      <div className="flex flex-1 flex-col p-4 min-w-0">
        {/* Event type badge with colour */}
        {firstEventType && (
          <span
            className="mb-2 w-fit rounded px-2 py-1 text-xs font-medium shadow-sm"
            style={{
              backgroundColor: eventTypeColor ? `${eventTypeColor}20` : 'hsl(var(--primary) / 0.1)',
              borderLeft: eventTypeColor
                ? `3px solid ${eventTypeColor}`
                : '3px solid hsl(var(--primary))',
            }}
          >
            {firstEventType.name}
          </span>
        )}

        {/* Title */}
        <h3 className="mb-2 line-clamp-2 text-lg font-semibold leading-tight transition-colors group-hover:text-primary break-words min-w-0">
          {event.title}
        </h3>

        {/* Date and time */}
        {startDate && (
          <div className="mb-3 space-y-1 text-sm text-muted-foreground min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <Calendar className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate min-w-0">
                {formatEventDate(startDate)}
                {endDate &&
                  endDate.toDateString() !== startDate.toDateString() &&
                  ` - ${formatEventDate(endDate)}`}
              </span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <Clock className="h-3.5 w-3.5 shrink-0" style={{ color: eventTypeColor || undefined }} />
              <span className="truncate min-w-0">
                {formatEventTime(startDate)}
                {endDate && ` - ${formatEventTime(endDate)}`}
              </span>
            </div>
          </div>
        )}

        {/* Location */}
        {event.eventLocation && (
          <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground min-w-0">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="line-clamp-1 min-w-0 break-words">{event.eventLocation}</span>
          </div>
        )}

        {/* Themes */}
        {event.theme && event.theme.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Tag className="h-3 w-3 text-muted-foreground" />
            {event.theme.slice(0, 2).map((theme) => (
              <span
                key={theme.id}
                className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
              >
                {theme.name}
              </span>
            ))}
            {event.theme.length > 2 && (
              <span className="text-xs text-muted-foreground">+{event.theme.length - 2}</span>
            )}
          </div>
        )}

        {/* Excerpt */}
        {/* {excerpt && (
          <p className="line-clamp-3 flex-1 text-sm text-muted-foreground">{excerpt}</p>
        )} */}

        {/* External Link */}
        {event.eventExternalLink?.url && (
          <div className="mt-auto border-t pt-3">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const url = event.eventExternalLink?.url;
                if (url) {
                  window.open(url, '_blank', 'noopener,noreferrer');
                }
              }}
              className="flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              <ExternalLink className="h-4 w-4" />
              <span>{event.eventExternalLink.title || 'External Link'}</span>
            </button>
          </div>
        )}

        {/* Contact */}
        {(event.eventContactName || event.eventContact?.name) && (
          <div className="mt-auto flex items-center gap-2 border-t pt-3 text-xs text-muted-foreground">
            <User className="h-3 w-3" />
            <span>{event.eventContactName || event.eventContact?.name}</span>
          </div>
        )}

        {/* Add to Calendar Button */}
        {startDate && (
          <div className="mt-auto border-t pt-3">
            <AddToCalendar
              event={{
                title: event.title,
                startDate: startDate,
                endDate: endDate,
                location: event.eventLocation || null,
                description: excerpt || null,
                url: eventUrl || null,
              }}
              variant="outline"
              size="sm"
              className="w-full"
            />
          </div>
        )}
      </div>
    </Link>
  );
}

/**
 * Default card layout - Non-clickable version with action buttons
 * Used for EventThemeCard where the entire card should not be clickable
 */
export function EventCardDefaultNonClickable({
  event,
  eventUrl,
  data,
  showPastIndicator = true,
}: {
  event: EventTeaser;
  eventUrl: string;
  data: EventCardData;
  showPastIndicator?: boolean;
}) {
  const { startDate, endDate, isPast, isToday, excerpt, eventTypeColor, firstEventType } = data;

  return (
    <div
      className={`flex h-full w-full flex-col overflow-hidden rounded-lg border shadow-sm transition-all hover:shadow-lg ${
        eventTypeColor ? '' : 'bg-card'
      }`}
      style={{
        backgroundColor: eventTypeColor
          ? darkenHexColorWithOpacity(eventTypeColor, 80, 0.1)
          : undefined,
      }}
    >
      {/* Image or date badge */}
      {event.image ? (
        <div className="relative h-24 p-2">
          {/* Date badge overlay */}
          {startDate && <EventDateBadgeOverlay date={startDate} />}

          {/* Status badges */}
          <div className="absolute right-4 top-4 flex flex-col gap-2">
            {isToday && (
              <span className="rounded bg-accent px-2 py-1 text-xs font-medium text-accent-foreground shadow-md">
                Today
              </span>
            )}
            {isPast && showPastIndicator && (
              <span className="rounded bg-muted px-2 py-1 text-xs font-medium text-muted-foreground shadow-md">
                Past
              </span>
            )}
          </div>
        </div>
      ) : startDate ? (
        <div className="flex h-32 items-center justify-center bg-linear-to-br from-primary/10 to-primary/5">
          <EventDateBadge date={startDate} size="large" className="shadow-md" />
        </div>
      ) : null}
      {/* Content */}
      <div className="flex flex-1 flex-col p-4 min-w-0">
        {/* Event type badge with colour */}
        {firstEventType && (
          <span
            className="mb-2 w-fit rounded px-2 py-1 text-xs font-medium shadow-sm"
            style={{
              backgroundColor: eventTypeColor ? `${eventTypeColor}20` : 'hsl(var(--primary) / 0.1)',
              borderLeft: eventTypeColor
                ? `3px solid ${eventTypeColor}`
                : '3px solid hsl(var(--primary))',
            }}
          >
            {firstEventType.name}
          </span>
        )}

        {/* Title */}
        <h3 className="mb-2 line-clamp-2 text-lg font-semibold leading-tight break-words min-w-0">
          {event.title}
        </h3>

        {/* Date and time */}
        {startDate && (
          <div className="mb-3 space-y-1 text-sm text-muted-foreground min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <Calendar className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate min-w-0">
                {formatEventDate(startDate)}
                {endDate &&
                  endDate.toDateString() !== startDate.toDateString() &&
                  ` - ${formatEventDate(endDate)}`}
              </span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <Clock className="h-3.5 w-3.5 shrink-0" style={{ color: eventTypeColor || undefined }} />
              <span className="truncate min-w-0">
                {formatEventTime(startDate)}
                {endDate && ` - ${formatEventTime(endDate)}`}
              </span>
            </div>
          </div>
        )}

        {/* Location */}
        {event.eventLocation && (
          <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground min-w-0">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="line-clamp-1 min-w-0 break-words">{event.eventLocation}</span>
          </div>
        )}

        {/* Themes */}
        {event.theme && event.theme.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Tag className="h-3 w-3 text-muted-foreground" />
            {event.theme.slice(0, 2).map((theme) => (
              <span
                key={theme.id}
                className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
              >
                {theme.name}
              </span>
            ))}
            {event.theme.length > 2 && (
              <span className="text-xs text-muted-foreground">+{event.theme.length - 2}</span>
            )}
          </div>
        )}

        {/* Contact */}
        {(event.eventContactName || event.eventContact?.name) && (
          <div className="mb-3 flex items-center gap-2 border-t pt-3 text-xs text-muted-foreground">
            <User className="h-3 w-3" />
            <span>{event.eventContactName || event.eventContact?.name}</span>
          </div>
        )}

        {/* Actions: Details button + Add to Calendar */}
        <div className="mt-auto  flex justify-between items-center gap-2">

          {startDate && (
            <AddToCalendar
              event={{
                title: event.title,
                startDate: startDate,
                endDate: endDate,
                location: event.eventLocation || null,
                description: excerpt || null,
                url: eventUrl || null,
              }}
              variant="outline"
              size="sm"
            />
          )}          
            <Link href={eventUrl} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
                Details
                <ArrowRight className="h-4 w-4" />
              </Link>
      
   
        </div>
      </div>
    </div>
  );
}
